import type { AxiosInstance, AxiosResponse } from 'axios'
import { useAxios, type EasyUseAxiosReturn, type UseAxiosReturn } from '@vueuse/integrations/useAxios'
import type { ApiClientCallConfig, UrlParams } from './types'

const prepareUrl = (url?: string, urlParams?: UrlParams) => {
    return url?.replace(/:[\w]+/g, (match: string) => {
        const name = match.replace(':', '')
        return urlParams ? urlParams[name] : ''
    }) || ''
}

export type UseApiClientReturnType<T, D = ANY> = UseAxiosReturn<T, AxiosResponse<T>, D> & {
    call: (config?: ApiClientCallConfig<D>) => Promise<AxiosResponse<T> | undefined>
} & Promise<EasyUseAxiosReturn<T, AxiosResponse<T>, D>>

export function useApiClient<T, D = ANY>(_config: ApiClientCallConfig<D>, axiosInstance: AxiosInstance): UseApiClientReturnType<T, D> {
    const config = { ... _config }
    const url = prepareUrl(config.url, config.urlParams)

    delete config.url

    const request = useAxios<T, AxiosResponse<T>, D>(url, config, axiosInstance, { immediate: false })

    function call(_overloadConfig?: ApiClientCallConfig<D>): Promise<AxiosResponse<T> | undefined> {
        let overloadConfig = _overloadConfig

        if (overloadConfig?.urlParams) {
            overloadConfig = {
                ..._overloadConfig,
                url: prepareUrl(
                    _config.url,
                    {
                        ..._config.urlParams,
                        ..._overloadConfig?.urlParams,
                    }),
            }
        }

        return new Promise((resolve, reject) => {
            request.execute(overloadConfig?.url || url, overloadConfig)
                .then((response) => {
                    if (request.error.value) {
                        reject(request.error.value)
                        return
                    }
                    resolve(response?.response?.value)
                })
                .catch(reject)
        })

    }

    return {
        ...request,
        call,
    }
}
